import type { Reader as StripeReader } from '@stripe/terminal-js'
import type { Ref, ComputedRef } from 'vue'
import { ref, computed, watchPostEffect } from 'vue'
import { isBooksyCom } from '@booksy-com/vue-tools'
import { isNil, cond, T as stubTrue } from 'ramda'

import {
  CONNECTIONS_STATUSES,
  LOCAL_STORAGE_LAST_READER_SN,
  READERS,
  READER_ACTION_STATUSES,
  READER_STATUSES,
} from '@/components/settings/payments/payment-types/booksy-card-reader/consts'
import { getBcrConnectInfo } from '@/lib/analytics/helpers'

import { useCore } from '@/composables/core'
import { useToast } from '@/composables/toast'
import { useAnalytics } from '@/composables/analytics'
import { useStorage } from '@/composables/storage'
import { useDevLogger } from '@/composables/dev-logger'
import { useStripeSdi } from './sdi'
import { createStripeSdk } from './sdk'
import { createStripeWebview } from './webView'
import { getStripeConnectionToken } from '@/api/get-stripe-connection-token/index'
import { getStripeReaders } from '@/api/get-stripe-readers/index'
import type { StripeReaderItem } from '@/api/types/stripe'

import {
  getPaymentIntent,
  cancelPaymentIntent,
} from '@/composables/payments/payment-intent-functions'

type StripeWebview = Awaited<ReturnType<typeof createStripeWebview>>
type StripeSdk = Awaited<ReturnType<typeof createStripeSdk>>
type StripeSdi = ReturnType<typeof useStripeSdi>

interface Reader extends StripeReader {
  isBluetooth?: boolean,
  name?: string
}

export interface UseBooksyCardReaderReturn {
  initialized: Ref<boolean>
  activeReader: ComputedRef<Reader | null>
  connectingReaderSN: ComputedRef<string | null>
  isSDIMode: StripeSdi['isSDIMode']
  switchIntegrationModeToSDI: StripeSdi['switchIntegrationModeToSDI']
  shouldSupportM2: ComputedRef<boolean>
  updateStatusM2: StripeWebview['updateStatus'] | undefined
  isBluetoothReader: (device_type: string) => boolean | undefined
  bluetoothReaders: ComputedRef<Ref<Reader[]> | Reader[] | undefined>
  bluetoothDiscoverReadersStart: StripeWebview['bluetoothDiscoverReadersStart'] | undefined
  bluetoothDiscoverReadersCancel: StripeWebview['bluetoothDiscoverReadersCancel'] | undefined
  simulatorConfig: StripeSdk['simulatorConfig']
  setSimulatedTerminal: StripeSdk['setSimulatedTerminal']
  collectPaymentMethod: StripeSdk['collectPaymentMethod']
  connectionStatus: ComputedRef<string | Ref<string>>
  paymentStatus: StripeSdk['paymentStatus']
  processPayment: StripeSdk['processPayment']
  cancelCollectPaymentMethod: StripeSdk['cancelCollectPaymentMethod']
  getPaymentIntent: typeof getPaymentIntent
  getRegisteredReaders: () => Promise<Reader[]>
  cancelPaymentIntent: typeof cancelPaymentIntent
  connectReader: (reader: Reader, label?: string) => Promise<void>
  connectLastReader: () => Promise<void>
  disconnectReader: () => Promise<void>
  handleUnexpectedTerminalDisconnect: () => Promise<void>
}

const logger = useDevLogger('booksy-card-reader')

let sdk: StripeSdk | null = null
let sdi: StripeSdi | null = null
const webView = ref<StripeWebview | null>(null)

let watchConnectionStatus: (() => void) | null = null

function assertStripeSdk (): StripeSdk {
  if (sdk === null) {
    throw new Error('Stripe Terminal SDK is not initialized')
  }
  return sdk
}

function assertStripeSdi (): StripeSdi {
  if (sdi === null) {
    throw new Error('Stripe SDI is not initialized')
  }
  return sdi
}

const initialized = ref(false)
const notSupported = ref(false)
// last serial number of connected reader
const lastReaderSN = ref<string | null>(null)
const connectingReaderSN = ref<string | null>(null)
const activeReader = ref<Reader | null>(null)

export async function createBooksyCardReader (): Promise<boolean | void> {
  logger.debug('initializing')

  if (initialized.value) throw new Error('BCR was already created')

  const { store } = useCore()

  const storeState = store.state as { routingConfig: { config: { pos__stripe_terminal?: unknown } }; business?: { pos?: { stripe_terminal_enabled?: boolean } } }
  const { pos__stripe_terminal } = storeState.routingConfig.config

  if (!(pos__stripe_terminal && storeState.business?.pos?.stripe_terminal_enabled)) {
    notSupported.value = true
    return false
  }

  // temporary move old connected SN to json strignify localstorage
  const storage = useStorage()
  const lastSN = localStorage.getItem(LOCAL_STORAGE_LAST_READER_SN)
  storage.setItem((storage.KEYS as { BCR_LAST_CONNECTED_READER_SN: string }).BCR_LAST_CONNECTED_READER_SN, lastSN)

  await store.dispatch('business/loadStripeAccount')

  sdk = await createStripeSdk({ getConnectionToken, handleUnexpectedTerminalDisconnect })
  sdi = useStripeSdi()

  try {
    const isTablet = store.getters['app/isTablet']
    if (isTablet) {
      webView.value = await createStripeWebview({ getConnectionToken, handleUnexpectedTerminalDisconnect })
    }
  } catch (error) {
    logger.error(error)
  }

  watchConnectionStatus = watchPostEffect(() => {
    const sdkStatus = typeof sdk?.connectionStatus === 'object' && sdk?.connectionStatus && 'value' in sdk.connectionStatus
      ? (sdk.connectionStatus as { value: string }).value
      : sdk?.connectionStatus
    const webViewStatus = typeof webView.value?.bluetoothConnectionStatus === 'object' && webView.value?.bluetoothConnectionStatus && 'value' in webView.value.bluetoothConnectionStatus
      ? (webView.value.bluetoothConnectionStatus as { value: string }).value
      : webView.value?.bluetoothConnectionStatus
    if (
      sdkStatus !== CONNECTIONS_STATUSES.CONNECTED
      || (webView.value && webViewStatus !== CONNECTIONS_STATUSES.CONNECTED)
    ) {
      activeReader.value = null
    }
  })

  lastReaderSN.value = storage.getItem((storage.KEYS as { BCR_LAST_CONNECTED_READER_SN: string }).BCR_LAST_CONNECTED_READER_SN) as string | null

  initialized.value = true
  logger.debug('initialized')

  try {
    await connectLastReader()
  } catch (error) {
    logger.error(error)
  }
}

export async function destroyBooksyCardReader (): Promise<void> {
  if (!initialized.value) return

  logger.debug('destroying')

  await disconnectReader()

  initialized.value = false
  lastReaderSN.value = null
  watchConnectionStatus?.()

  sdk?.destroyService()
  webView.value?.destroyService()

  sdk = null
  sdi = null
  webView.value = null
}

export function useBooksyCardReader (): UseBooksyCardReaderReturn | void {
  if (notSupported.value) return
  if (!initialized.value) {
    throw new Error('useBooksyCardReader called before createBooksyCardReader')
  }

  const stripeSdk = assertStripeSdk()
  const stripeSdi = assertStripeSdi()

  const connectionStatus = computed(() => {
    const isWebViewCheckingStatus = () => {
      const status = webView.value?.bluetoothConnectionStatus
      const statusVal = typeof status === 'object' && status && 'value' in status ? (status as { value: string }).value : status
      return !!webView.value && statusVal !== CONNECTIONS_STATUSES.NOT_CONNECTED
    }
    const isSdkCheckingStatus = () => {
      const status = stripeSdk.connectionStatus
      return (typeof status === 'object' && status && 'value' in status ? (status as { value: string }).value : status) !== CONNECTIONS_STATUSES.NOT_CONNECTED
    }
    const isSDICheckingStatus = () => stripeSdi.isSDIMode.value && activeReader.value

    const statusGetter = cond([
      [isWebViewCheckingStatus, () => {
        const wv = webView.value
        if (!wv) {
          return CONNECTIONS_STATUSES.NOT_CONNECTED
        }
        return wv.bluetoothConnectionStatus
      }],
      [isSdkCheckingStatus, () => {
        const status = stripeSdk.connectionStatus
        return typeof status === 'object' && status && 'value' in status ? (status as { value: string }).value : status
      }],
      [isSDICheckingStatus, () => CONNECTIONS_STATUSES.CONNECTED],
      [stubTrue, () => CONNECTIONS_STATUSES.NOT_CONNECTED],
    ])

    return statusGetter()
  })

  return {
    initialized,
    activeReader: computed(() => activeReader.value),
    connectingReaderSN: computed(() => connectingReaderSN.value),
    isSDIMode: stripeSdi.isSDIMode,
    switchIntegrationModeToSDI: stripeSdi.switchIntegrationModeToSDI,
    shouldSupportM2: computed(() => !isNil(webView.value)),
    updateStatusM2: webView.value?.updateStatus,
    isBluetoothReader: webView.value?.isBluetoothReader || (() => false),
    bluetoothReaders: computed(() => webView.value?.readers as unknown as Ref<Reader[]> | undefined),
    bluetoothDiscoverReadersStart: webView.value?.bluetoothDiscoverReadersStart,
    bluetoothDiscoverReadersCancel: webView.value?.bluetoothDiscoverReadersCancel,
    simulatorConfig: stripeSdk.simulatorConfig,
    setSimulatedTerminal: stripeSdk.setSimulatedTerminal,
    collectPaymentMethod: stripeSdk.collectPaymentMethod,
    connectionStatus,
    paymentStatus: stripeSdk.paymentStatus,
    processPayment: stripeSdk.processPayment,
    cancelCollectPaymentMethod: stripeSdk.cancelCollectPaymentMethod,
    getPaymentIntent,
    getRegisteredReaders,
    cancelPaymentIntent,
    connectReader,
    connectLastReader,
    disconnectReader,
    // exported only for testing purposes
    handleUnexpectedTerminalDisconnect,
  }
}

async function getConnectionToken () {
  const { store } = useCore()
  const businessId = store.getters['business/businessId']

  const response = await getStripeConnectionToken(businessId)

  return response.data.connection_token
}

async function handleUnexpectedTerminalDisconnect (): Promise<void> {
  try {
    if (sdk?.wifiCheckoutPending.value) return

    logger.debug('Unexpected terminal disconnect. Trying to connect to the previous reader')
    await connectLastReader()
  } catch (error) {
    _resetReaderState()
    logger.error(error)
  }
}

async function connectLastReader () {
  logger.debug(`connectLastReader: ${lastReaderSN.value}`)

  if (!lastReaderSN.value) {
    logger.debug('lastReaderSN not set')
    return
  }

  const readers = await getRegisteredReaders()

  const reader = readers.find(reader => reader.serial_number === lastReaderSN.value)
  if (!reader) {
    throw Error('last reader not found in registeredReaders')
  }

  if (reader.isBluetooth) {
    if (!webView.value) throw Error('M2 not supported, native app update required!')

    try {
      await webView.value.checkPermissions()
    } catch (error) {
      logger.debug('Bluetooth or location permissions not granted. Please grant them in the app settings')
      throw error
    }
  }

  await connectReader(reader, undefined)
}

async function connectReader (reader: Reader, label?: string): Promise<void> {
  logger.debug(`connecting: ${reader.serial_number}`)

  await disconnectReader()

  connectingReaderSN.value = String(reader.serial_number)

  try {
    const readers = await getRegisteredReaders()
    const stripeSdi = assertStripeSdi()
    if (!reader.isBluetooth && stripeSdi.isSDIMode.value) {
      await stripeSdi.checkIfReaderIsOnline(readers, reader)
    } else {
      const found = readers.find(r => r.serial_number === reader.serial_number)
      const id = found?.id ?? reader.id
      await _connectWebViewOrSDKReader(reader, id, label)
    }

    activeReader.value = {
      ...reader,
      label: label || reader.label,
    }
    lastReaderSN.value = String(reader.serial_number)

    const storage = useStorage()
    storage.setItem((storage.KEYS as { BCR_LAST_CONNECTED_READER_SN: string }).BCR_LAST_CONNECTED_READER_SN, String(reader.serial_number))

    logger.debug(`set default reader: ${reader.serial_number}`)
  } finally {
    connectingReaderSN.value = null
  }
}

interface ConnectResult {
  error?: { message?: string; code?: string }
  error_description?: string
}

async function _connectWebViewOrSDKReader (reader: Reader, id: string, label?: string): Promise<void> {
  const stripeSdk = assertStripeSdk()
  let connectResult: ConnectResult | undefined
  if (reader.isBluetooth) {
    const wv = webView.value
    if (!wv) {
      throw new Error('WebView is required to connect a Bluetooth reader')
    }
    connectResult = await wv.connect(reader as Reader) as ConnectResult | undefined
  } else {
    connectResult = await stripeSdk.connect(reader as Reader) as ConnectResult | undefined
  }

  const connectionInfo = getBcrConnectInfo(reader)
  const error = connectResult?.error || connectResult?.error_description
  const analytics = useAnalytics()
  const { EVENTS } = analytics

  if (error) {
    analytics.dispatch(EVENTS.BCR_CARD_READER_CONNECT, {
      ...connectionInfo,
      stripe_bcr_failure_reason: (error as { message?: string }).message,
      stripe_bcr_failure_code: (error as { code?: string }).code,
    })
    throw error
  }
  analytics.dispatch(EVENTS.BCR_CARD_READER_CONNECT, connectionInfo)

  stripeSdk.setSimulatedTerminal({ reader: reader as Reader })

  if (webView.value && label) {
    await webView.value.setReaderLabel(String(reader.serial_number), label, id)
  }
}

async function getRegisteredReaders (): Promise<Reader[]> {
  const { store } = useCore()
  const businessId = store.getters['business/businessId']
  const isTablet = store.getters['app/isTablet']
  const stripeSdk = assertStripeSdk()
  const stripeSdi = assertStripeSdi()

  const { data } = await getStripeReaders(businessId)

  const busyReadersSN: string[] = []
  const readers = data.readers
    .filter((reader: StripeReaderItem) => {
      const readers = Object.values(READERS)
      const READER = readers.find(READER => READER.device_type === reader.device_type)
      return READER && !READER.disabled && (isTablet || !READER.bluetooth)
    })
    .map((reader: StripeReaderItem) => {
      const isBluetooth = !!webView.value?.isBluetoothReader(reader.device_type)
      const isBusy = !isBluetooth && stripeSdi.isSDIMode.value && reader.action?.status === READER_ACTION_STATUSES.IN_PROGRESS
      if (isBusy) {
        busyReadersSN.push(String(reader.serial_number))
        stripeSdi.checkReaderBusyTime(reader as unknown as Reader)
      }
      return {
        ...reader,
        isBluetooth,
        status: isBusy ? READER_STATUSES.BUSY : reader.status,
      } as Reader
    })

  if (!busyReadersSN.length) {
    const storage = useStorage()
    storage.removeItem((storage.KEYS as { BCR_BUSY_READERS: string }).BCR_BUSY_READERS)
  }

  if (activeReader.value && busyReadersSN.includes(String(activeReader.value.serial_number))) {
    await disconnectReader()
  }

  if (isTablet && !isBooksyCom()) {
    readers.push({
      status: READER_STATUSES.OFFLINE,
      serial_number: 'STRM2SIMULATOR1',
      location: 'st_simulated',
      device_type: 'stripe_m2',
      isBluetooth: true,
    } as Reader)
  }

  if (stripeSdk.simulatorConfig.simulated && !isBooksyCom()) {
    const simulatedData = await stripeSdk.discoverReaders({
      simulated: stripeSdk.simulatorConfig.simulated,
    }) as { discoveredReaders?: Reader[] } | undefined
    if (simulatedData?.discoveredReaders?.length) readers.push(simulatedData.discoveredReaders[0])
  }

  return readers
}

async function disconnectReader (): Promise<void> {
  logger.debug(`disconnecting: ${activeReader.value?.serial_number}`)

  const { store } = useCore()
  const toast = useToast()
  const isTablet = store.getters['app/isTablet']

  if (isTablet && webView.value) {
    await webView.value.disconnect()
  }

  const stripeSdi = assertStripeSdi()
  const stripeSdk = assertStripeSdk()

  if (!stripeSdi.isSDIMode.value) {
    try {
      await stripeSdk.disconnect()
    } catch (error) {
      logger.error(error)
      toast.error('Failed to disconnect reader')
    }
  }

  _resetReaderState()
}

function _resetReaderState (): void {
  logger.debug('reseting reader state')
  connectingReaderSN.value = null
  activeReader.value = null
  webView.value?.resetUpdateState()
  assertStripeSdk().wifiCheckoutPending.value = false
}
